import React from 'react';
import { ProductType, ProductVariant } from '../common/CommonTypes';
import { getLinkedProductNames } from '../common/ProductMethods';
import PriceDisplay from '../common/PriceDisplay';
import PriceTotal from './PriceTotal';
import { PaymentMethod } from '../common/Constants';
import { ProductChannel } from '../../consts/Common';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import { t } from 'i18next';
import { getSelectedNoOfInstallments } from '../common/HelperMethods';

type PaymentSummaryProps = {
  products: ProductType[];
  hasSubscription: boolean;
  hasMonthly: boolean;
  displayCustomerFriendlyName?: boolean;
  hideInstallmentLabel?: boolean;
  subscriptionTotalLabel?: string;
  oneOffPaymentLabel?: string;
  productChannel: number;
  culture: string;
};

const PaymentSummary = ({
  products,
  hasSubscription,
  hasMonthly,
  displayCustomerFriendlyName,
  hideInstallmentLabel,
  subscriptionTotalLabel,
  oneOffPaymentLabel,
  productChannel,
  culture
}: PaymentSummaryProps): JSX.Element => {
  const isRenewal = productChannel === ProductChannel.RenewalJourney;

  const selectedCardProducts = products.filter(
    (product) =>
      product.isSelected &&
      (product.selectedPaymentMethod === PaymentMethod.Subscription ||
        product.selectedPaymentMethod === PaymentMethod.Single)
  );

  const renderSinglePayProducts = () => {
    const singlePayProducts = selectedCardProducts.filter(
      (product) => product.selectedPaymentMethod === PaymentMethod.Single
    );

    return (
      <div>
        <div className='m2-top m1-bottom'>
          {singlePayProducts.map((product: ProductType) =>
            renderProduct(product, PaymentMethod.Single)
          )}
        </div>
        <PriceTotal
          products={singlePayProducts}
          subscriptionTotalLabel={subscriptionTotalLabel}
          oneOffPaymentLabel={oneOffPaymentLabel}
          hideInstallmentLabel={hideInstallmentLabel}
          culture={culture}
        />
      </div>
    );
  };

  const renderPaymentAssistProducts = () => {
    const paymentAssistProducts = selectedCardProducts.filter(
      (product) => product.selectedPaymentMethod === PaymentMethod.PaymentAssist
    );

    return (
      <div>
        <div className='m2-top m1-bottom'>
          {paymentAssistProducts.map((product: ProductType) =>
            renderProduct(product, PaymentMethod.PaymentAssist)
          )}
        </div>
        <PriceTotal
          products={paymentAssistProducts}
          subscriptionTotalLabel={subscriptionTotalLabel}
          oneOffPaymentLabel={oneOffPaymentLabel}
          hideInstallmentLabel={hideInstallmentLabel}
          culture={culture}
        />
      </div>
    );
  };

  const renderSubscriptionProducts = () => {
    if (!hasSubscription) {
      return <></>;
    }

    const subscriptionProducts = selectedCardProducts.filter(
      (product) => product.selectedPaymentMethod === PaymentMethod.Subscription
    );

    return (
      <div>
        <div className='m2-top m1-bottom'>
          {subscriptionProducts.map((product: ProductType) =>
            renderProduct(product, PaymentMethod.Subscription)
          )}
        </div>
        <PriceTotal
          products={subscriptionProducts}
          subscriptionTotalLabel={subscriptionTotalLabel}
          oneOffPaymentLabel={oneOffPaymentLabel}
          hideInstallmentLabel={hideInstallmentLabel}
          culture={culture}
        />
      </div>
    );
  };

  const renderMonthlyProducts = () => {
    if (!hasMonthly) {
      return <></>;
    }

    const monthlyProducts = products.filter(
      (product) =>
        product.isSelected &&
        product.selectedPaymentMethod === PaymentMethod.Monthly
    );

    const monthlyProductMessage = hasSubscription
      ? t('customer-product-page.subscription-price')
      : '';

    return (
      <div>
        <div className='m2-top m1-bottom'>
          {monthlyProducts.map((product: ProductType) =>
            renderProduct(product, PaymentMethod.Monthly)
          )}
        </div>
        <PriceTotal products={monthlyProducts} culture={culture} />
        <div className='p3'>{monthlyProductMessage}</div>
        <hr />
      </div>
    );
  };

  const renderProduct = (product: ProductType, paymentMethod: string) => {
    const selectedVariant = product.productVariants.find(
      (v) => v.paymentMethod === paymentMethod && v.isSelected === true
    );

    if (selectedVariant === undefined) {
      return <></>;
    }

    const name =
      displayCustomerFriendlyName === true
        ? selectedVariant.customerFriendlyName
        : product.productName;

    let titleStyle = 'p1';
    if (isRenewal === true) {
      titleStyle += ' bold';
    }

    return (
      <div key={`total-${product.productTypeId}`}>
        <div className='price-total-container'>
          <div className='price-total-label'>
            <p className={titleStyle}>{name}</p>
            {renderLinkedItems(product, selectedVariant)}
          </div>
          {!isRenewal && (
            <div className='mobile-top-margin'>
              <PriceDisplay
                product={product}
                noOfInstallments={getSelectedNoOfInstallments(product)}
                className='p1'
                hideInstallmentLabel={hideInstallmentLabel}
                selectedLinkedProducts={product.selectedLinkedProducts ?? []}
                culture={culture}
              />
            </div>
          )}
        </div>
        <hr />
      </div>
    );
  };

  const renderLinkedItems = (
    product: ProductType,
    selectedVariant: ProductVariant
  ) => {
    const result = getLinkedProductNames(
      product.productId,
      selectedVariant.linkedProducts,
      product.selectedLinkedProducts ?? []
    );

    if (CommonValidation.isNotEmpty(result)) {
      return <p>Including {result}</p>;
    }
  };

  return (
    <>
      <div>{renderSubscriptionProducts()}</div>
      <div>{renderMonthlyProducts()}</div>
      <div>{renderSinglePayProducts()}</div>
      <div>{renderPaymentAssistProducts()}</div>
    </>
  );
};

export default PaymentSummary;
